import React, { useContext } from "react";

import classnames from "classnames";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import FormAddLanguage from "./FormAddLanguage";
import { FormTranslationsContext } from "../../contexts/FormTranslationsContext";

import type { JSX } from "react";

import type { LanguageCode } from "../../api/ProjectAPI.types";
import type { PageFieldArray } from "../../contexts/FormTranslationsContext";

type TranslationStatus = "error" | "dirty" | "saved";

type DirtyFieldValue =
  | boolean
  | string
  | DirtyFieldValue[]
  | [string, DirtyFieldValue];

function computeTranslationStatus(
  errors: { translations?: Record<number, unknown> },
  dirtyFields: { translations?: Record<string, DirtyFieldValue>[] },
  index: number,
): TranslationStatus {
  if (errors.translations?.[index]) return "error";
  const fieldDirty = dirtyFields.translations?.[index];

  /**
   * Remove translation status for picture.
   * They are always uploaded and don't have a local state.
   * */
  delete fieldDirty?.picture;

  const getIsDirty = (fields: DirtyFieldValue[]): boolean =>
    fields
      .map((value) => {
        if (Array.isArray(value)) {
          return getIsDirty(value);
        }

        if (typeof value === "object" && value !== null) {
          return getIsDirty(Object.entries(value));
        }

        return value === true;
      })
      .some(Boolean);

  const isDirty = getIsDirty(Object.entries(fieldDirty ?? {}));

  if (isDirty) return "dirty";

  return "saved";
}

interface FormLanguageListProps {
  languageList?: LanguageCode[];
}

function LanguageDot({
  status,
}: {
  status: "error" | "dirty" | "saved";
}): JSX.Element {
  const { t } = useTranslation("languages");
  const colorClass = {
    error: "bg-destructive",
    dirty: "bg-status-pause",
    saved: "bg-status-active",
  }[status];

  const tooltipContent = {
    error: t("languageTooltips.requiredField"),
    dirty: t("languageTooltips.dirtyTranslation"),
    saved: t("languageTooltips.savedLanguage"),
  }[status];

  const dotClassName = classnames(
    "absolute -right-1 -top-1 size-[0.46875rem] rounded-full",
    colorClass,
  );

  return (
    <div
      className={dotClassName}
      data-tooltip-content={tooltipContent}
      data-tooltip-id={APP_TOOLTIP_ID}
    />
  );
}

export default function FormLanguageList({
  languageList,
}: FormLanguageListProps): JSX.Element | null {
  const { t } = useTranslation("languages");

  const { fieldArray, activeLanguageIndex, setActiveLanguageIndex } =
    useContext(FormTranslationsContext);

  const { formState } = useFormContext<PageFieldArray>();
  const { errors, dirtyFields } = formState;

  if (!fieldArray) return null;

  return (
    <div className="border-border bg-muted flex flex-wrap items-stretch gap-[0.625rem] rounded-[0.3125rem] border p-[0.3125rem]">
      {fieldArray.fields.map((field, index) => {
        const isActive = index === activeLanguageIndex;
        const isDefault = field.default;

        return (
          <div
            key={field.id}
            data-tooltip-content={t(`langs.${field.language}`)}
            data-tooltip-id={APP_TOOLTIP_ID}
            onClick={(): void => setActiveLanguageIndex(index)}
            className={classnames(
              "relative flex min-w-[45px] cursor-pointer flex-col items-center justify-center rounded-lg border border-transparent px-[0.625rem] py-[0.234375rem] text-center text-sm transition-colors",
              isActive && "text-primary border-border bg-card drop-shadow-sm",
              !isActive &&
                "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
            )}
          >
            <div className={classnames("font-semibold uppercase")}>
              {field.language}
            </div>
            {isDefault ? (
              <div
                className={classnames(
                  "-mt-1 text-[10px] text-nowrap",
                  isActive ? "text-muted-foreground" : "text-muted-foreground",
                )}
              >
                {t("default")}
              </div>
            ) : null}
            <LanguageDot
              status={computeTranslationStatus(errors, dirtyFields, index)}
            />
          </div>
        );
      })}
      <FormAddLanguage languageList={languageList} />
    </div>
  );
}
